import { useState } from 'react'
import {
  Box,
  Typography,
  Button,
  Paper,
  Container,
  TextField,
  Alert,
  CircularProgress,
  Link,
  useTheme
} from '@mui/material'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { useAuth } from '@shared/auth/AuthContext'
import { useNavigate, useLocation } from 'react-router-dom'

const loginSchema = yup.object({
  email: yup
    .string()
    .required('Email is required')
    .email('Please enter a valid email address'),
  password: yup
    .string()
    .required('Password is required')
    .min(8, 'Password must be at least 8 characters')
})

interface LoginFormData {
  email: string
  password: string
}

function LoginPage() {
  const theme = useTheme()
  const navigate = useNavigate()
  const location = useLocation()
  const { login, loginWithMfa } = useAuth()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [requiresMfa, setRequiresMfa] = useState(false)
  const [loginEmail, setLoginEmail] = useState('')
  const [mfaCode, setMfaCode] = useState('')

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<LoginFormData>({
    resolver: yupResolver(loginSchema),
    mode: 'onBlur'
  })

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true)
    setError(null)

    try {
      const result = await login(data)

      if (result.success) {
        // Redirect to the page they were trying to access, or home
        const from = (location.state as any)?.from?.pathname || '/'
        navigate(from, { replace: true })
      } else if (result.requiresMfa) {
        // Show MFA verification dialog
        setLoginEmail(data.email)
        setRequiresMfa(true)
      } else {
        setError(result.message)
      }
    } catch (err) {
      setError('Failed to sign in. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleMfaSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setError(null)

    try {
      const result = await loginWithMfa({ email: loginEmail, code: mfaCode })

      if (result.success) {
        // Redirect to the page they were trying to access, or home
        const from = (location.state as any)?.from?.pathname || '/'
        navigate(from, { replace: true })
      } else {
        setError(result.message)
      }
    } catch (err) {
      setError('MFA verification failed. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleBackToLogin = () => {
    setRequiresMfa(false)
    setMfaCode('')
    setError(null)
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      <Container maxWidth="sm">
        <Paper sx={{ p: 6, borderRadius: 2 }}>
          {!requiresMfa ? (
            <>
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
                  Welcome Back
                </Typography>
                <Typography variant="h6" color="text.secondary">
                  Sign in to your Helpdesk Pro account
                </Typography>
              </Box>

              {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {error}
                </Alert>
              )}

              <form onSubmit={handleSubmit(onSubmit)}>
                <TextField
                  fullWidth
                  label="Email Address"
                  type="email"
                  {...register('email')}
                  error={!!errors.email}
                  helperText={errors.email?.message}
                  disabled={isLoading}
                  sx={{ mb: 3 }}
                />

                <TextField
                  fullWidth
                  label="Password"
                  type="password"
                  {...register('password')}
                  error={!!errors.password}
                  helperText={errors.password?.message}
                  disabled={isLoading}
                  sx={{ mb: 3 }}
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={isLoading}
                  sx={{
                    py: 1.5,
                    fontSize: '1.1rem',
                    fontWeight: 'bold',
                    mb: 3
                  }}
                >
                  {isLoading ? (
                    <>
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                      Signing In...
                    </>
                  ) : (
                    'Sign In'
                  )}
                </Button>
              </form>

              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Don't have an account?{' '}
                  <Link
                    component="button"
                    onClick={() => navigate('/signup')}
                    sx={{ fontWeight: 'bold', textDecoration: 'none' }}
                  >
                    Start your free trial
                  </Link>
                </Typography>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  <Link
                    component="button"
                    onClick={() => navigate('/forgot-password')}
                    sx={{ textDecoration: 'none' }}
                  >
                    Forgot your password?
                  </Link>
                </Typography>

                <Typography variant="body2" color="text.secondary" sx={{ mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                  <Link
                    href="https://github.com/taylin-studio/helpdesk/blob/main/docs/QUICK-START-GUIDE.md"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ textDecoration: 'none', fontWeight: 'medium' }}
                  >
                    📖 Need help? View Quick Start Guide
                  </Link>
                </Typography>
              </Box>
            </>
          ) : (
            <>
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
                  Two-Factor Authentication
                </Typography>
                <Typography variant="h6" color="text.secondary">
                  Enter the verification code from your authenticator app
                </Typography>
              </Box>

              {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {error}
                </Alert>
              )}

              <form onSubmit={handleMfaSubmit}>
                <TextField
                  fullWidth
                  label="Verification Code"
                  type="text"
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value)}
                  placeholder="Enter 6-digit code"
                  disabled={isLoading}
                  autoFocus
                  sx={{ mb: 3 }}
                  inputProps={{
                    maxLength: 6,
                    pattern: '[0-9]*',
                    inputMode: 'numeric'
                  }}
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={isLoading || mfaCode.length === 0}
                  sx={{
                    py: 1.5,
                    fontSize: '1.1rem',
                    fontWeight: 'bold',
                    mb: 2
                  }}
                >
                  {isLoading ? (
                    <>
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                      Verifying...
                    </>
                  ) : (
                    'Verify Code'
                  )}
                </Button>

                <Button
                  fullWidth
                  variant="text"
                  onClick={handleBackToLogin}
                  disabled={isLoading}
                >
                  Back to Login
                </Button>
              </form>

              <Box sx={{ mt: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Lost your device? Recovery codes also work in the verification code field above.
                </Typography>
              </Box>
            </>
          )}
        </Paper>
      </Container>
    </Box>
  )
}

export default LoginPage